import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import {
  CZESTOTLIWOSCI,
  OKRESY,
  utworzHarmonogram,
  zmienHarmonogram,
  type Czestotliwosc,
  type Harmonogram,
  type Okres,
  type ZapisHarmonogramu,
} from '@/lib/harmonogramy-api';
import { useI18n } from '@/lib/i18n';
import { WyborKolumn } from '@/components/WyborKolumn';
import { zestawStartowy } from '@/lib/kolumny-raportu';
import { katalogKolumn, type FormatPliku, type NazwaRaportu } from '@/lib/raporty-api';

/**
 * DEFINICJA HARMONOGRAMU — jedno okno na wszystko, co wykonuje się potem bez człowieka.
 *
 * ⚠️ NIE MA TU POLA „OD–DO" I NIE BĘDZIE. Zamiast zakresu jest OKRES („poprzedni tydzień"),
 * liczony przy każdym uruchomieniu. Zapisana data znaczyłaby, że tygodniówka do końca świata
 * wysyła ten sam tydzień — plik przychodzi, wygląda poprawnie i nikt tego nie zauważy.
 *
 * ⚠️ KOLUMNY BIERZEMY Z KATALOGU SERWERA, tak samo jak w oknie raportu własnego. Bez tego ekran
 * pozwoliłby zapisać kolumnę, której serwer nie zna — i harmonogram wywracałby się o 6:00
 * na odmowie, którą człowiek zobaczyłby tydzień później w historii przebiegów.
 */
export function EdytorHarmonogramu({
  groupId,
  harmonogram,
  onClose,
  onZapisano,
}: {
  groupId: string;
  /** `null` = nowy harmonogram. */
  harmonogram: Harmonogram | null;
  onClose: () => void;
  onZapisano: () => void;
}) {
  const { t } = useI18n();
  const [blad, setBlad] = useState<string | null>(null);

  const [nazwa, setNazwa] = useState(harmonogram?.name ?? '');
  const [raport, setRaport] = useState<NazwaRaportu>(harmonogram?.report ?? 'transactions');
  const [format, setFormat] = useState<FormatPliku>(harmonogram?.format ?? 'xlsx');
  /**
   * NAGŁÓWKI W `.txt` — PER TEN HARMONOGRAM.
   *
   * ⚠️⚠️ ROZSTRZYGNIĘTE PRZEZ UŻYTKOWNIKA (31.08.2026): „to nie może być globalne ustawienie, tylko
   * per raport". Wcześniej stało to w Ustawieniach wyspy i było źle postawionym pytaniem: na jednej
   * wyspie jeden raport jedzie do PROGRAMU, który nagłówka nie chce, a drugi do CZŁOWIEKA, który
   * bez nagłówka nie wie, co czyta.
   */
  const [naglowkiTxt, setNaglowkiTxt] = useState(harmonogram?.naglowkiTxt ?? true);
  const [okres, setOkres] = useState<Okres | ''>((harmonogram?.period as Okres | null) ?? 'poprzedniTydzien');
  const [czestotliwosc, setCzestotliwosc] = useState<Czestotliwosc>(harmonogram?.frequency ?? 'TYGODNIOWO');
  const [godzina, setGodzina] = useState(String(harmonogram?.hour ?? 6));
  const [minuta, setMinuta] = useState(String(harmonogram?.minute ?? 0));
  const [dzienTygodnia, setDzienTygodnia] = useState(String(harmonogram?.weekday ?? 1));
  const [dzienMiesiaca, setDzienMiesiaca] = useState(String(harmonogram?.dayOfMonth ?? 1));
  const [odbiorcy, setOdbiorcy] = useState((harmonogram?.recipients ?? []).join(', '));
  const [aktywny, setAktywny] = useState(harmonogram?.active ?? true);
  const [kolumny, setKolumny] = useState<string[] | null>(harmonogram?.columns ?? null);

  /**
   * ⚠️ KATALOG PRZEŁADOWUJE SIĘ PO ZMIANIE RAPORTU, a wybór kolumn leci do zera. Kolumny wydań
   * nie istnieją w zatowarowaniu — przeniesione „na wszelki wypadek" dałyby odmowę serwera przy
   * pierwszym przebiegu, czyli błąd widoczny dopiero w historii.
   */
  const katalog = useQuery({ queryKey: ['raport-kolumny', raport], queryFn: () => katalogKolumn(raport) });

  /**
   * ⚠️ ZESTAW USTALAMY RAZ, GDY KATALOG DOJDZIE, i od tego momentu należy do człowieka. Liczenie
   * go przy każdym renderze (tak było, gdy okno tylko WYŚWIETLAŁO kolumny) kasowałoby każdą zmianę
   * natychmiast po kliknięciu — wybieraczka wyglądałaby na zepsutą.
   */
  useEffect(() => {
    if (katalog.data === undefined || kolumny !== null) return;
    setKolumny(zestawStartowy(katalog.data.kolumny, katalog.data.domyslne, harmonogram?.columns ?? null));
  }, [katalog.data, harmonogram, kolumny]);

  const wybraneKolumny = kolumny ?? [];

  const dane = (): ZapisHarmonogramu => ({
    name: nazwa.trim(),
    report: raport,
    format,
    naglowkiTxt,
    columns: wybraneKolumny,
    /** Zatowarowanie mówi o stanie NA TERAZ, więc okresu nie ma — i serwer go tam nie wymaga. */
    period: raport === 'transactions' ? ((okres === '' ? 'poprzedniTydzien' : okres) as Okres) : null,
    recipients: odbiorcy
      .split(/[,;\s]+/)
      .map((a) => a.trim())
      .filter((a) => a !== ''),
    frequency: czestotliwosc,
    hour: Number(godzina),
    minute: Number(minuta),
    weekday: czestotliwosc === 'TYGODNIOWO' ? Number(dzienTygodnia) : null,
    dayOfMonth: czestotliwosc === 'MIESIECZNIE' ? Number(dzienMiesiaca) : null,
    active: aktywny,
  });

  const zapisz = useMutation({
    mutationFn: () =>
      harmonogram === null ? utworzHarmonogram(groupId, dane()) : zmienHarmonogram(harmonogram.id, dane()),
    onSuccess: onZapisano,
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const mozna = nazwa.trim() !== '' && wybraneKolumny.length > 0 && !zapisz.isPending;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      {/*
        ⚠️ TO OKNO JEST SZERSZE OD DOMYŚLNYCH 32rem, i to jest cała poprawka układu wiersza „Kiedy".
        Najdłuższy wariant — „co tydzień" + „w poniedziałek" + „o godzinie 06:00" — potrzebuje około
        495 px treści, a przy 32rem zostaje ich 472 (`p-5` z dwóch stron). Brakowało więc dwudziestu
        kilku pikseli i dlatego blok czasu spadał do drugiego wiersza; 36rem daje 536 px, czyli zapas
        także na dłuższe tłumaczenia. Reszta okna nic na tym nie traci — pola i tak są pełnej szerokości.
        `cn` w `DialogContent` idzie przez `tailwind-merge`, więc ta klasa NADPISUJE domyślną szerokość,
        a nie dokłada się do niej.
      */}
      <DialogContent
        title={harmonogram === null ? t('harm.dodaj') : t('harm.edytuj')}
        description={t('harm.edytorOpis')}
        className="w-[min(36rem,calc(100vw-2rem))]"
      >
        <FormGrid>
          <Field label={t('harm.nazwa')} htmlFor="nazwa" required>
            <Input id="nazwa" value={nazwa} onChange={(e) => setNazwa(e.target.value)} placeholder={t('harm.nazwaHint')} />
          </Field>

          <Field label={t('harm.raport')} htmlFor="raport">
            <Select
              id="raport"
              value={raport}
              onChange={(e) => {
                setRaport(e.target.value as NazwaRaportu);
                setKolumny(null);
              }}
            >
              <option value="transactions">{t('harm.raport.transactions')}</option>
              <option value="stanAutomatow">{t('harm.raport.stanAutomatow')}</option>
            </Select>
          </Field>

          {/* Okres dotyczy TYLKO wydań — zatowarowanie opisuje stan na teraz. */}
          {raport === 'transactions' && (
            <Field label={t('harm.okresPole')} htmlFor="okres">
              <Select id="okres" value={okres} onChange={(e) => setOkres(e.target.value as Okres)}>
                {OKRESY.map((o) => (
                  <option key={o} value={o}>
                    {t(`harm.okres.${o}`)}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">{t('harm.okresHint')}</p>
            </Field>
          )}

          <Field label={t('harm.format')} htmlFor="format">
            {/*
              ⚠️ KOLEJNOŚĆ CSV, TXT, XLSX — ta sama co w paskach eksportu (zgłoszone 31.08.2026:
              „trzeba ujednolicić kolejność i nazwy plików"). PDF-a nie ma, bo plik tego formatu
              składa przeglądarka, a harmonogram powstaje na serwerze.

              ⚠️ `txt` DOŁOŻONE TU 31.08.2026: serwer przyjmował ten format od początku, ale
              wybieraczka go nie oferowała — czyli raport tab delimited dawał się pobrać z ekranu
              i NIE dawał się zaplanować, choć po to właśnie ten format powstał.
            */}
            <Select id="format" value={format} onChange={(e) => setFormat(e.target.value as FormatPliku)}>
              <option value="csv">CSV</option>
              <option value="txt">TXT</option>
              <option value="xlsx">XLSX</option>
            </Select>
            {/*
              ⚠️ OPCJA POKAZUJE SIĘ TYLKO PRZY `txt`, ale WARTOŚĆ ZOSTAJE ZAPISANA zawsze: w CSV
              i XLSX nagłówek jest z definicji, więc pytanie byłoby tam bez treści — a schowanie
              pola nie ma prawa cofać raz podjętej decyzji.
            */}
            {format === 'txt' && (
              <div className="mt-2 grid gap-1">
                <Select
                  aria-label={t('harm.naglowkiTxt')}
                  value={naglowkiTxt ? 'tak' : 'nie'}
                  onChange={(e) => setNaglowkiTxt(e.target.value === 'tak')}
                >
                  <option value="tak">{t('harm.naglowkiTxtTak')}</option>
                  <option value="nie">{t('harm.naglowkiTxtNie')}</option>
                </Select>
                <p className="text-xs text-muted-foreground">{t('harm.naglowkiTxtHint')}</p>
              </div>
            )}
          </Field>

          <Field label={t('harm.kiedy')} htmlFor="czestotliwosc">
            <div className="flex flex-wrap items-center gap-2">
              <Select
                id="czestotliwosc"
                className="w-auto"
                value={czestotliwosc}
                onChange={(e) => setCzestotliwosc(e.target.value as Czestotliwosc)}
              >
                {CZESTOTLIWOSCI.map((c) => (
                  <option key={c} value={c}>
                    {t(`harm.czestotliwoscNazwa.${c}`)}
                  </option>
                ))}
              </Select>

              {czestotliwosc === 'TYGODNIOWO' && (
                <Select className="w-auto" value={dzienTygodnia} onChange={(e) => setDzienTygodnia(e.target.value)}>
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <option key={d} value={d}>
                      {t(`harm.dzien.${d}`)}
                    </option>
                  ))}
                </Select>
              )}

              {czestotliwosc === 'MIESIECZNIE' && (
                /** ⚠️ Do 28: harmonogram na 31. albo pomijałby luty, albo cicho przesuwał okres. */
                <Select className="w-auto" value={dzienMiesiaca} onChange={(e) => setDzienMiesiaca(e.target.value)}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {t('harm.dniaMiesiaca').replace('{n}', String(d))}
                    </option>
                  ))}
                </Select>
              )}

              {/*
                ⚠️ GODZINA I MINUTA W JEDNEJ, NIEROZŁĄCZNEJ GRUPIE — poprawka zgłoszenia „nie mieści
                nam się godzina obok siebie". Wszystkie pola stały w jednym `flex-wrap`, więc przy
                szerokości modala (32rem) zawijanie wypadało MIĘDZY godziną a minutą: „06" zostawało
                na końcu wiersza, a „00" spadało samo pod spód i przestawało wyglądać na czas.
                Zawijamy więc CAŁY blok czasu razem z jego podpisem, a w środku nie ma gdzie pęknąć.
                Dwukropek zamiast odstępu, żeby dwa pola czytały się jako jedna godzina.
              */}
              <span className="flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground">
                {t('harm.oGodzinie')}
                <Select className="w-auto" value={godzina} onChange={(e) => setGodzina(e.target.value)}>
                  {Array.from({ length: 24 }, (_, i) => i).map((g) => (
                    <option key={g} value={g}>
                      {String(g).padStart(2, '0')}
                    </option>
                  ))}
                </Select>
                <span aria-hidden>:</span>
                <Select className="w-auto" value={minuta} onChange={(e) => setMinuta(e.target.value)}>
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>
                      {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </Select>
              </span>
            </div>
            {/* ⚠️ Godzina jest LOKALNA DLA WYSPY, nie dla przeglądarki — i trzeba to powiedzieć. */}
            <p className="mt-1 text-xs text-muted-foreground">{t('harm.godzinaHint')}</p>
          </Field>

          <Field label={t('harm.odbiorcy')} htmlFor="odbiorcy">
            <Input
              id="odbiorcy"
              value={odbiorcy}
              onChange={(e) => setOdbiorcy(e.target.value)}
              placeholder="brygadzista@firma.pl, magazyn@firma.pl"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('harm.odbiorcyHint')}</p>
          </Field>

          {/*
            ⚠️ TA SAMA WYBIERACZKA CO W OKNIE RAPORTU WŁASNEGO — jeden komponent, nie dwa układy tej
            samej listy. Wcześniej okno tylko POKAZYWAŁO kolumny i odsyłało do tamtego okna, ale
            tamto zapisuje wybór w pamięci PRZEGLĄDARKI, nie w harmonogramie: raport wysyłany co
            tydzień jechał więc zawsze zestawem domyślnym, mimo że ekran sugerował coś innego.
          */}
          <Field label={t('harm.kolumny')} htmlFor="kolumny">
            {katalog.isPending && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
            {katalog.data !== undefined && (
              <WyborKolumn
                katalog={katalog.data.kolumny}
                domyslne={katalog.data.domyslne}
                wybrane={wybraneKolumny}
                onZmiana={setKolumny}
                wysokoscWybranych="max-h-36"
              />
            )}
            {/* Odróżnienie od okna raportu własnego, które pamięta wybór tylko w przeglądarce. */}
            <p className="mt-1 text-xs text-muted-foreground">{t('harm.kolumnyHint')}</p>
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={aktywny} onChange={(e) => setAktywny(e.target.checked)} />
            {t('harm.aktywny')}
          </label>

          <ModalError>{blad}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!mozna} onClick={() => zapisz.mutate()}>
            {zapisz.isPending && <Loader2 className="animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
